/**
 * Waypoint Utilities
 *
 * Shared utilities for extracting and mapping waypoint data between
 * Action protobuf trees and UI representations.
 *
 * @remarks
 * These utilities are used by both cloud web and mobile platforms to ensure
 * consistent waypoint data extraction and camera action mapping.
 */

import { PanoramaMode_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/take_panorama/take_panorama";
import { HeadingMode_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";

import type { Action } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";

/**
 * Camera action types supported by waypoint editor
 *
 * @remarks
 * Uses PanoramaMode_Enum values for type safety and consistency with missionStore
 */
export type CameraActionType =
  | "none"
  | "takePhoto"
  | PanoramaMode_Enum.HORIZONTAL
  | PanoramaMode_Enum.VERTICAL
  | PanoramaMode_Enum.SPHERE;

/**
 * In-transit camera action types
 */
export type InTransitCameraActionType = "none" | "startVideo" | "intervalPhotos";

/**
 * Extracted values from a waypoint Action tree
 *
 * @remarks
 * All angles are in degrees. Heights and speeds are in meters and m/s respectively.
 */
export interface WaypointValues {
  /** Latitude in degrees */
  latitude: number;
  /** Longitude in degrees */
  longitude: number;
  /** Height above takeoff point in meters */
  height: number;
  /** Heading in degrees (0-360, 0 = North) */
  heading: number;
  /** Gimbal pitch in degrees (-90 to 30, negative points down) */
  gimbalPitch: number;
  /** Transit speed in m/s */
  speed: number;
  /** Pause time at waypoint in seconds */
  pauseTime: number;
  /** Camera action to perform at waypoint */
  cameraAction: CameraActionType | null;
  /** Heading mode for in-transit behavior */
  headingMode: HeadingMode_Enum;
  /** Camera action during transit to waypoint */
  inTransitCameraAction: InTransitCameraActionType;
}

/**
 * Picker index values for camera actions
 *
 * @remarks
 * Used by both mobile FsPage picker and cloud React picker to ensure
 * consistent ordering across platforms.
 */
export enum CameraActionPickerIndex {
  NONE = 0,
  PHOTO = 1,
  HORIZONTAL_PANO = 2,
  VERTICAL_PANO = 3,
  SPHERE_PANO = 4,
}

/**
 * Extract in-transit camera action from waypoint's sequence actions
 *
 * @param waypoint - Waypoint Action containing sequence of actions
 * @returns In-transit camera action type
 */
function getInTransitCameraAction(waypoint: Action): InTransitCameraActionType {
  const actions = waypoint.args?.sequence?.actions;
  if (!actions) return "none";

  // Look for startVideo or togglePhotoIntervalMode actions in the sequence
  for (const action of actions) {
    if (action.args?.startVideo) {
      return "startVideo";
    }
    if (action.args?.togglePhotoIntervalMode?.enable === true) {
      return "intervalPhotos";
    }
  }

  return "none";
}

/**
 * Extract camera action from waypoint's sequence actions
 *
 * @param waypoint - Waypoint Action containing sequence of actions
 * @returns Camera action type or null if none
 */
function getCameraActionFromWaypoint(waypoint: Action): CameraActionType | null {
  const actions = waypoint.args?.sequence?.actions;
  if (!actions) return null;

  // Camera actions are typically in the sequence after GotoWaypoint and TimedWait
  for (const action of actions) {
    if (action.args?.takePhoto) {
      return "takePhoto";
    }
    if (action.args?.takePanorama) {
      const mode = action.args.takePanorama.panoramaMode;
      if (mode === PanoramaMode_Enum.HORIZONTAL) return PanoramaMode_Enum.HORIZONTAL;
      if (mode === PanoramaMode_Enum.VERTICAL) return PanoramaMode_Enum.VERTICAL;
      if (mode === PanoramaMode_Enum.SPHERE) return PanoramaMode_Enum.SPHERE;
    }
  }

  return null;
}

/**
 * Extract all waypoint values from Action protobuf tree
 *
 * @remarks
 * Navigates the nested Action structure to extract waypoint parameters.
 * Converts radians to degrees for heading and gimbalPitch.
 * Handles GotoWaypoint action (index 2) and TimedWait action (index 3).
 *
 * @param waypoint - Waypoint Action containing nested sequence
 * @returns Extracted waypoint values with all angles in degrees
 */
export function extractWaypointValues(waypoint: Action): WaypointValues {
  const actions = waypoint.args?.sequence?.actions;
  const gotoWaypointAction = actions?.[2];
  const timedWaitAction = actions?.[3];
  const wp = gotoWaypointAction?.args?.gotoWaypoint?.waypoint;

  // Extract heading mode from motion args
  const lookAtArgs = gotoWaypointAction?.args?.gotoWaypoint?.motionArgs?.lookAtArgs;
  const headingMode = lookAtArgs?.headingMode ?? HeadingMode_Enum.LOOKAHEAD;

  // Extract in-transit camera action
  const inTransitCameraAction = getInTransitCameraAction(waypoint);

  return {
    latitude: wp?.xy?.x ?? 0,
    longitude: wp?.xy?.y ?? 0,
    height: wp?.z?.value ?? 50,
    heading: (wp?.heading?.value ?? 0) * (180 / Math.PI), // radians to degrees
    gimbalPitch: (wp?.gimbalPitch?.value ?? -Math.PI / 4) * (180 / Math.PI), // radians to degrees
    speed: gotoWaypointAction?.args?.gotoWaypoint?.motionArgs?.traversalArgs?.speed ?? 5,
    pauseTime: timedWaitAction?.args?.timedWait?.timeout?.value ?? 0,
    cameraAction: getCameraActionFromWaypoint(waypoint),
    headingMode,
    inTransitCameraAction,
  };
}

/**
 * Convert camera action type to picker index
 *
 * @remarks
 * Used when populating UI with existing waypoint data.
 * Returns NONE (0) for unknown action types.
 *
 * @param action - Camera action type or null
 * @returns Picker index (0-4)
 */
export function cameraActionToPickerIndex(action: CameraActionType | null): number {
  if (!action || action === "none") return CameraActionPickerIndex.NONE;
  if (action === "takePhoto") return CameraActionPickerIndex.PHOTO;
  if (action === PanoramaMode_Enum.HORIZONTAL) return CameraActionPickerIndex.HORIZONTAL_PANO;
  if (action === PanoramaMode_Enum.VERTICAL) return CameraActionPickerIndex.VERTICAL_PANO;
  if (action === PanoramaMode_Enum.SPHERE) return CameraActionPickerIndex.SPHERE_PANO;
  return CameraActionPickerIndex.NONE;
}

/**
 * Convert picker index to camera action type
 *
 * @remarks
 * Used when user changes camera action selection in UI.
 * Returns "none" for invalid indices.
 *
 * @param index - Picker selection index
 * @returns Camera action type
 */
export function pickerIndexToCameraAction(index: number): CameraActionType {
  switch (index) {
    case CameraActionPickerIndex.PHOTO:
      return "takePhoto";
    case CameraActionPickerIndex.HORIZONTAL_PANO:
      return PanoramaMode_Enum.HORIZONTAL;
    case CameraActionPickerIndex.VERTICAL_PANO:
      return PanoramaMode_Enum.VERTICAL;
    case CameraActionPickerIndex.SPHERE_PANO:
      return PanoramaMode_Enum.SPHERE;
    default:
      return "none";
  }
}
